import { NewCoreIconProps } from '@atlaskit/icon';
import AlignLeftIcon from '@atlaskit/icon/core/align-left';
import NumberIcon from '@atlaskit/icon/core/hashtag';
import ListBulletedIcon from '@atlaskit/icon/core/list-bulleted';
import ArrowLeftCircleIcon from '@atlaskit/icon/core/migration/arrow-left--arrow-left-circle';
import CalendarIcon from '@atlaskit/icon/core/migration/calendar--calendar-filled';
import CheckCircleIcon from '@atlaskit/icon/core/migration/check-circle--check-circle-outline';
import CheckboxCheckedIcon from '@atlaskit/icon/core/migration/checkbox-checked--checkbox';
import ImageIcon from '@atlaskit/icon/core/migration/image--media-services-scale-large';
import IssueIcon from '@atlaskit/icon/core/migration/issue';
import IssuesIcon from '@atlaskit/icon/core/migration/issues';
import LinkIcon from '@atlaskit/icon/core/migration/link';
import PageIcon from '@atlaskit/icon/core/migration/page';
import PersonIcon from '@atlaskit/icon/core/person';
import NewTagIcon from '@atlaskit/icon/core/tag';
import OldArrowLeftCircleIcon from '@atlaskit/icon/glyph/arrow-left-circle';
import CalendarFilledIcon from '@atlaskit/icon/glyph/calendar-filled';
import CheckCircleOutlineIcon from '@atlaskit/icon/glyph/check-circle-outline';
import CheckboxIcon from '@atlaskit/icon/glyph/checkbox';
import ChevronDownCircleIcon from '@atlaskit/icon/glyph/chevron-down-circle';
import EditorMentionIcon from '@atlaskit/icon/glyph/editor/mention';
import EditorTextStyleIcon from '@atlaskit/icon/glyph/editor/text-style';
import OldIssueIcon from '@atlaskit/icon/glyph/issue';
import OldIssuesIcon from '@atlaskit/icon/glyph/issues';
import LabelIcon from '@atlaskit/icon/glyph/label';
import OldLinkIcon from '@atlaskit/icon/glyph/link';
import MediaServicesScaleLargeIcon from '@atlaskit/icon/glyph/media-services/scale-large';
import OldPageIcon from '@atlaskit/icon/glyph/page';
import OldSearchIcon from '@atlaskit/icon/glyph/search';
import BookOpenIcon from '@atlaskit/icon-lab/core/book-open';
import LozengeIcon from '@atlaskit/icon-lab/core/lozenge';
import Tooltip from '@atlaskit/tooltip';
import { DatabaseFieldType } from '@databases/types/database';
import React, { ComponentType, FunctionComponent } from 'react';
import { useIntl, MessageDescriptor } from 'react-intl-next';

import {
  EntryOutlineIcon,
  InfoOutlineIcon,
  LegacyEntryIconOutline,
  NumberIcon as OldNumberIcon,
} from './icons';

export interface DatabaseFieldIconType {
  icon: ComponentType<NewCoreIconProps>;
  // icon to render when ADS visual refresh is turned off
  fallbackIcon?: NewCoreIconProps['LEGACY_fallbackIcon'];
}

export const FIELD_ICONS: { [Type in DatabaseFieldType]: DatabaseFieldIconType } = {
  [DatabaseFieldType.TEXT]: {
    icon: AlignLeftIcon,
    // eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
    fallbackIcon: EditorTextStyleIcon,
  },
  [DatabaseFieldType.ATTACHMENT]: {
    icon: ImageIcon,
    // eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
    fallbackIcon: MediaServicesScaleLargeIcon,
  },
  [DatabaseFieldType.NUMBER]: {
    icon: NumberIcon,
    fallbackIcon: OldNumberIcon,
  },
  [DatabaseFieldType.DATE]: {
    icon: CalendarIcon,
    // eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
    fallbackIcon: CalendarFilledIcon,
  },
  [DatabaseFieldType.SELECT]: {
    icon: NewTagIcon,
    // eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
    fallbackIcon: ChevronDownCircleIcon,
  },
  [DatabaseFieldType.USER]: {
    icon: PersonIcon,
    // eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
    fallbackIcon: EditorMentionIcon,
  },
  [DatabaseFieldType.HYPERLINK]: {
    icon: LinkIcon,
    // eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
    fallbackIcon: OldLinkIcon,
  },
  [DatabaseFieldType.CHECKBOX]: {
    icon: CheckboxCheckedIcon,
    // eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
    fallbackIcon: CheckboxIcon,
  },
  [DatabaseFieldType.PAGE_LINK]: {
    icon: PageIcon,
    // eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
    fallbackIcon: OldPageIcon,
  },
  [DatabaseFieldType.PAGE_STATUS]: {
    icon: CheckCircleIcon,
    // eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
    fallbackIcon: CheckCircleOutlineIcon,
  },
  [DatabaseFieldType.PAGE_LABELS]: {
    icon: LozengeIcon,
    // eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
    fallbackIcon: LabelIcon,
  },
  [DatabaseFieldType.PAGE_DETAILS]: {
    icon: BookOpenIcon,
    fallbackIcon: InfoOutlineIcon,
  },
  [DatabaseFieldType.ENTRY_LINK]: {
    icon: EntryOutlineIcon,
    fallbackIcon: LegacyEntryIconOutline,
  },
  [DatabaseFieldType.ENTRY_DETAILS]: {
    icon: ListBulletedIcon,
    // eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
    fallbackIcon: OldSearchIcon,
  },
  [DatabaseFieldType.BACKLINK]: {
    icon: ArrowLeftCircleIcon, // for now, should eventually be https://product-fabric.atlassian.net/browse/NCTD-3472
    // eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
    fallbackIcon: OldArrowLeftCircleIcon,
  },
  [DatabaseFieldType.JIRA_ISSUE]: {
    icon: IssueIcon,
    // eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
    fallbackIcon: OldIssueIcon,
  },
  [DatabaseFieldType.JIRA_ISSUE_DETAILS]: {
    icon: IssuesIcon,
    // eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
    fallbackIcon: OldIssuesIcon,
  },
};

export interface FieldTypeIconProps {
  type: DatabaseFieldType;
  label?: MessageDescriptor;
  noTooltip?: boolean;
}

/**
 * Renders an icon that indicates the specified field type. A tooltip text will describe the field type (unless disabled).
 */
export const FieldTypeIcon: FunctionComponent<FieldTypeIconProps> = ({
  type,
  label,
  noTooltip,
}) => {
  const { formatMessage } = useIntl();

  const iconData = FIELD_ICONS[type];
  const icon = (
    <iconData.icon
      label=""
      LEGACY_size="small"
      LEGACY_fallbackIcon={iconData.fallbackIcon}
      testId={`field-type-icon-${type}`}
    />
  );

  if (noTooltip || !label) {
    return icon;
  }

  const formattedLabel = formatMessage(label);

  return (
    <Tooltip tag="span" content={formattedLabel}>
      {icon}
    </Tooltip>
  );
};
